import { getSelectedKeyboardLayoutConfig } from "../../../lib/utils";
import {
  escapeHtml,
  mountHQPanel,
  numberOr,
  removeHQPanel,
  resolveKeyForLayout,
} from "./hqShared.js";

const CARD_BG = "#FFFFFF";
const EMOTION_IMAGE_HEIGHT_PX = 42;
const EMOTION_BLOCK_HEIGHT_PX = 36;
const EMOTION_KEY_GAP_PX = 2;
const EMOTION_GROUP_GAP_PERCENT = 2;
const KEY_CODES = ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"];
const EMOTION_NAMES = ["emote-happy", "emote-surprise", "emote-trust", "emote-sad", "emote-angry", "emote-disgust"];

/**
 * Builds the HTML string for the survey panel shown in HQ context.
 *
 * @param {JsPsych} jsPsych
 * @param {Object} config
 * @param {string} [config.portrait="hq-happy"]
 * @param {string} [config.promptText="How are you?"]
 * @param {string} [config.emotionImageBasePath="assets/images/headquarter/custom"]
 * @param {string[]} [config.emotionNames=["emote-happy","emote-surprise","emote-trust","emote-sad","emote-angry","emote-disgust"]]
 * @param {number} [config.widthPx=790]
 * @param {string} [config.maxWidth="60%"]
 * @param {number} [config.heightPx=100]
 * @param {number} [config.minHeightPx=70]
 * @param {number} [config.portraitSizePx=70]
 * @param {number} [config.emotionImageHeightPx=42]
 * @param {number} [config.emotionKeyStrokePx=6]
 * @param {number} [config.emotionGroupGapPercent=2]
 * @param {number} [config.promptMinFontSizePx=9]
 * @param {number} [config.promptFluidVw=1.2]
 * @param {number} [config.promptFontSizePx=20]
 * @param {number} [config.keyFontSizePx=32]
 * @returns {string}
 */
export function buildSurveyPanelHTML(jsPsych, config = {}) {
  const layoutConfig = getSelectedKeyboardLayoutConfig(jsPsych);

  const {
    portrait = "hq-happy",
    promptText = "How are you?",
    emotionImageBasePath = "assets/images/headquarter/custom",
    emotionNames = EMOTION_NAMES,
    widthPx = 790,
    maxWidth = "60%",
    heightPx = 100,
    minHeightPx = 70,
    portraitSizePx = 70,
    emotionImageHeightPx = EMOTION_IMAGE_HEIGHT_PX,
    emotionKeyStrokePx = 2,
    emotionGroupGapPercent = EMOTION_GROUP_GAP_PERCENT,
    promptMinFontSizePx = 4,
    promptFluidVw = 1.5,
    promptFontSizePx = 20,
    keyFontSizePx = 32,
  } = config;

  const cardHeightCss = Number.isFinite(heightPx) ? `${heightPx}px` : "auto";
  const cardMinHeightCss = Number.isFinite(heightPx) ? `${heightPx}px` : `${numberOr(minHeightPx, 70)}px`;
  const portraitRenderSizePx = portraitSizePx + 25;
  const cardMaxWidthCss = maxWidth === "none" ? `${widthPx}px` : `min(${widthPx}px, ${maxWidth})`;
  const surveyPromptMaxSizePx = numberOr(promptFontSizePx, 20);
  const configuredPromptMinSizePx = numberOr(promptMinFontSizePx, 9);
  const surveyPromptMinSizePx = Math.max(6, Math.min(configuredPromptMinSizePx, surveyPromptMaxSizePx));
  const surveyPromptFluidVw = Math.max(0.4, Math.min(2.2, numberOr(promptFluidVw, 1.2)));
  const surveyPromptSizeCss = `clamp(${surveyPromptMinSizePx}px, ${surveyPromptFluidVw}vw, ${surveyPromptMaxSizePx}px)`;
  const surveyKeyMaxSizePx = numberOr(keyFontSizePx, 32);
  const surveyKeySizeCss = `${surveyKeyMaxSizePx}px`;
  const surveyImageHeightPx = numberOr(emotionImageHeightPx, EMOTION_IMAGE_HEIGHT_PX);
  const surveyKeyStrokePx = numberOr(emotionKeyStrokePx, 1.5);
  const surveyGroupGapPercent = Math.max(0, Math.min(12, numberOr(emotionGroupGapPercent, EMOTION_GROUP_GAP_PERCENT)));
  const emotionList = Array.isArray(emotionNames) && emotionNames.length === KEY_CODES.length
    ? emotionNames
    : EMOTION_NAMES;

  const portraitSrc = escapeHtml(`assets/images/instructions/custom/${portrait}.svg`);
  const keyLabels = KEY_CODES.map((code) => escapeHtml(resolveKeyForLayout(code, layoutConfig)));
  const emotionBlocksHtml = keyLabels
    .map((label, index) => {
      const emotionName = String(emotionList[index] ?? "");
      const emotionSrc = escapeHtml(`${emotionImageBasePath}/${emotionName}.svg`);
      return `
        <div class="gear-hq-emotion-block">
          <img src="${emotionSrc}" alt="${escapeHtml(emotionName)}" class="gear-hq-emotion-image" />
          <div class="gear-hq-emotion-key">
            <span class="gear-hq-emotion-key-text">${label}</span>
          </div>
        </div>`;
    })
    .join("");

  return `
    <div class="gear-hq-card" style="
      --hq-card-max-width:${cardMaxWidthCss};
      --hq-card-height:${cardHeightCss};
      --hq-card-min-height:${cardMinHeightCss};
      --hq-portrait-size:${portraitRenderSizePx}px;
      --hq-survey-prompt-size:${surveyPromptSizeCss};
      --hq-survey-image-height:${surveyImageHeightPx}px;
      --hq-survey-key-block-height:${EMOTION_BLOCK_HEIGHT_PX}px;
      --hq-survey-key-gap:${EMOTION_KEY_GAP_PX}px;
      --hq-survey-key-size:${surveyKeySizeCss};
      --hq-survey-key-stroke:${surveyKeyStrokePx}px;
      --hq-survey-group-gap:${surveyGroupGapPercent}%;
      --hq-survey-columns:repeat(${KEY_CODES.length}, minmax(0, 1fr));
      background:${CARD_BG};
    ">
      <div class="gear-hq-portrait-shell">
        <img
          src="${portraitSrc}"
          alt="HQ portrait"
          class="gear-hq-portrait-img"
        />
      </div>

      <div class="gear-hq-survey-body">
        <div class="gear-hq-survey-prompt">${escapeHtml(promptText)}</div>

        <div class="gear-hq-survey-choices-wrap">
          <div class="gear-hq-survey-choices-grid">${emotionBlocksHtml}</div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Injects the HQ survey panel into the footer area.
 *
 * @param {JsPsych} jsPsych
 * @param {Object} config
 * @returns {HTMLElement|null}
 */
export function injectSurveyPanel(jsPsych, config = {}) {
  const verticalOffsetPx = Number.isFinite(config?.verticalOffsetPx)
    ? config.verticalOffsetPx
    : -6;
  return mountHQPanel({
    panelId: "hq-survey-panel",
    html: buildSurveyPanelHTML(jsPsych, config),
    verticalOffsetPx,
  });
}

/**
 * Removes the injected HQ survey panel from the DOM.
 */
export function removeSurveyPanel() {
  removeHQPanel("hq-survey-panel");
}
